import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { builderSectionCatalog } from '@shared/builder/document';
import type {
  AiGenerateBuilderPageResult,
  AiModelRecord,
  AiProviderId,
  AiProviderKeyStatusRecord,
  AiTaskKind,
  BuilderDocument,
  BuilderSection,
  BuilderThemePresetId,
  WorkspaceSettingsRecord
} from '@shared/types';
import { Icon } from '@renderer/shared/components/icons';
import { TextAreaField } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { useUiStore } from '@renderer/stores/ui-store';
import {
  editBuilderSectionWithAi,
  generateBuilderPageWithAi,
  getAiProviderKeyStatus,
  listAiModels
} from '@renderer/domains/ai/ai-repository';
import { getWorkspaceSettings } from '@renderer/domains/settings/settings-repository';
import { useProjectBuilderStore } from './project-builder-store';

type BuilderChatMode = Extract<AiTaskKind, 'full_design' | 'edit_section'>;

interface PendingProposal {
  mode: BuilderChatMode;
  pageId: string;
  sectionId?: string | null;
  document?: BuilderDocument;
  section?: BuilderSection;
  cacheHit: boolean;
  latencyMs: number;
}

interface ProjectBuilderAiBubbleProps {
  projectId: string;
  themePresetId: BuilderThemePresetId;
}

function getSectionLabel(section: BuilderSection | undefined, t: ReturnType<typeof useI18n>['t']): string {
  if (!section) {
    return '-';
  }

  const entry = builderSectionCatalog.find((candidate) => candidate.type === section.type);
  return entry ? t(entry.labelKey) : section.type;
}

const EMPTY_AI_KEY_STATUS: AiProviderKeyStatusRecord = {
  anthropic: { state: 'not_set', message: null },
  openai: { state: 'not_set', message: null }
};

function getProviderFallbackModelId(provider: AiProviderId): string {
  return provider === 'openai' ? 'gpt-5.4-mini' : 'claude-4.6-sonnet';
}

function pickPreferredModel(
  models: AiModelRecord[],
  availableProviders: AiProviderId[],
  workspaceSettings: WorkspaceSettingsRecord | null
): AiModelRecord | null {
  const available = models.filter((model) => availableProviders.includes(model.provider));
  if (available.length === 0) {
    return models[0] ?? null;
  }

  if (workspaceSettings) {
    const savedDefault = available.find((model) => model.id === workspaceSettings.avi.defaultModel);
    if (savedDefault) {
      return savedDefault;
    }

    const providerFallback = available.find(
      (model) =>
        model.provider === workspaceSettings.avi.defaultProvider &&
        model.id === getProviderFallbackModelId(workspaceSettings.avi.defaultProvider)
    );
    if (providerFallback) {
      return providerFallback;
    }

    const providerMatch = available.find((model) => model.provider === workspaceSettings.avi.defaultProvider);
    if (providerMatch) {
      return providerMatch;
    }
  }

  return available.find((model) => model.id === 'gpt-5.4-mini') ?? available[0] ?? models[0] ?? null;
}

export function ProjectBuilderAiBubble({ projectId, themePresetId }: ProjectBuilderAiBubbleProps) {
  const { t, locale } = useI18n();
  const addToast = useUiStore((state) => state.addToast);

  const selectedPageId = useProjectBuilderStore((state) => state.selectedPageId);
  const selectedSectionId = useProjectBuilderStore((state) => state.selectedSectionId);
  const pages = useProjectBuilderStore((state) => state.pages);
  const applyAiDocument = useProjectBuilderStore((state) => state.applyAiDocument);
  const applyAiSection = useProjectBuilderStore((state) => state.applyAiSection);
  const undoAiChange = useProjectBuilderStore((state) => state.undoAiChange);
  const aiUndoDepth = useProjectBuilderStore((state) => state.aiUndoStack.length);

  const activePage = useMemo(
    () => pages.find((record) => record.id === selectedPageId) ?? null,
    [pages, selectedPageId]
  );
  const activeSection = useMemo(
    () => activePage?.document.sections.find((section) => section.id === selectedSectionId) ?? null,
    [activePage, selectedSectionId]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<BuilderChatMode>('full_design');
  const [prompt, setPrompt] = useState('');
  const [models, setModels] = useState<AiModelRecord[]>([]);
  const [keyStatus, setKeyStatus] = useState<AiProviderKeyStatusRecord>(EMPTY_AI_KEY_STATUS);
  const [workspaceSettings, setWorkspaceSettings] = useState<WorkspaceSettingsRecord | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [pending, setPending] = useState<PendingProposal | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const promptRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const [loadedModels, loadedStatus, loadedWorkspaceSettings] = await Promise.all([
          listAiModels(),
          getAiProviderKeyStatus(),
          getWorkspaceSettings()
        ]);

        if (cancelled) {
          return;
        }

        setModels(loadedModels);
        setKeyStatus(loadedStatus);
        setWorkspaceSettings(loadedWorkspaceSettings);

        const availableProviders: AiProviderId[] = (['anthropic', 'openai'] as AiProviderId[]).filter(
          (provider) => loadedStatus[provider].state === 'saved'
        );
        const preferred = pickPreferredModel(loadedModels, availableProviders, loadedWorkspaceSettings);
        setSelectedModelId((current) => {
          if (
            current &&
            loadedModels.some(
              (model) => model.id === current && availableProviders.includes(model.provider)
            )
          ) {
            return current;
          }
          return preferred?.id ?? '';
        });
      } catch (loadError) {
        if (!cancelled) {
          setError((loadError as Error).message);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const availableProviders = useMemo<AiProviderId[]>(() => {
    return (['anthropic', 'openai'] as AiProviderId[]).filter((provider) => keyStatus[provider].state === 'saved');
  }, [keyStatus]);

  const activeModels = useMemo(() => {
    if (!models.length) {
      return [];
    }
    return models.filter((model) => availableProviders.includes(model.provider));
  }, [models, availableProviders]);

  const selectedModel = useMemo(
    () => models.find((model) => model.id === selectedModelId) ?? null,
    [models, selectedModelId]
  );

  useEffect(() => {
    if (!isOpen || activeModels.length === 0) {
      return;
    }

    if (selectedModelId && activeModels.some((model) => model.id === selectedModelId)) {
      return;
    }

    const preferred = pickPreferredModel(models, availableProviders, workspaceSettings);
    if (preferred) {
      setSelectedModelId(preferred.id);
    }
  }, [activeModels, availableProviders, isOpen, models, selectedModelId, workspaceSettings]);

  const hasAnyKey = availableProviders.length > 0;
  const requiresSection = mode === 'edit_section';
  const canSubmit =
    !isGenerating &&
    hasAnyKey &&
    Boolean(activePage) &&
    (!requiresSection || Boolean(activeSection)) &&
    prompt.trim().length > 0;

  const handleToggleOpen = useCallback(() => {
    setIsOpen((current) => !current);
    setError(null);
    setStatusMessage(null);
  }, []);

  const handleModeChange = useCallback((nextMode: BuilderChatMode) => {
    setMode(nextMode);
    setPending(null);
    setError(null);
    setStatusMessage(null);
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!activePage || !hasAnyKey) {
      return;
    }

    if (requiresSection && !activeSection) {
      setError(t('project.builder.ai.errors.noSection'));
      return;
    }

    setIsGenerating(true);
    setError(null);
    setStatusMessage(t('project.builder.ai.status.loading'));
    setPending(null);

    try {
      if (mode === 'full_design') {
        const result: AiGenerateBuilderPageResult = await generateBuilderPageWithAi({
          projectId,
          pageId: activePage.id,
          prompt: prompt.trim(),
          pageType: activePage.document.pageMeta.pageType,
          themePresetId,
          tone: activePage.document.themeRefs.tone,
          locale,
          model: selectedModelId || null
        });

        setPending({
          mode,
          pageId: activePage.id,
          document: result.document,
          cacheHit: result.usage.cacheHit,
          latencyMs: result.usage.latencyMs
        });
        setStatusMessage(result.usage.cacheHit ? t('project.builder.ai.status.cached') : t('project.builder.ai.status.ready'));
        return;
      }

      if (!activeSection) {
        setError(t('project.builder.ai.errors.noSection'));
        return;
      }

      const result = await editBuilderSectionWithAi({
        projectId,
        pageId: activePage.id,
        sectionId: activeSection.id,
        instruction: prompt.trim(),
        model: selectedModelId || null
      });

      setPending({
        mode,
        pageId: activePage.id,
        sectionId: activeSection.id,
        section: result.section,
        cacheHit: result.usage.cacheHit,
        latencyMs: result.usage.latencyMs
      });
      setStatusMessage(result.usage.cacheHit ? t('project.builder.ai.status.cached') : t('project.builder.ai.status.ready'));
    } catch (generationError) {
      const message = (generationError as Error).message || t('project.builder.ai.errors.generic');
      setError(message);
      setStatusMessage(null);
    } finally {
      setIsGenerating(false);
    }
  }, [
    activePage,
    activeSection,
    hasAnyKey,
    locale,
    mode,
    projectId,
    prompt,
    requiresSection,
    selectedModelId,
    t,
    themePresetId
  ]);

  const handleApply = useCallback(() => {
    if (!pending) {
      return;
    }

    if (pending.mode === 'full_design' && pending.document) {
      applyAiDocument(pending.pageId, pending.document);
    } else if (pending.mode === 'edit_section' && pending.section && pending.sectionId) {
      applyAiSection(pending.pageId, pending.sectionId, pending.section);
    }

    setPending(null);
    setPrompt('');
    setStatusMessage(t('project.builder.ai.status.applied'));
    addToast({
      title: t('project.builder.toast.title'),
      message: t('project.builder.ai.status.applied'),
      tone: 'success'
    });
  }, [addToast, applyAiDocument, applyAiSection, pending, t]);

  const handleDiscard = useCallback(() => {
    setPending(null);
    setStatusMessage(null);
  }, []);

  const handleUndo = useCallback(() => {
    const ok = undoAiChange();
    if (ok) {
      setStatusMessage(t('project.builder.ai.status.undone'));
      addToast({
        title: t('project.builder.toast.title'),
        message: t('project.builder.ai.status.undone'),
        tone: 'neutral'
      });
    }
  }, [addToast, t, undoAiChange]);

  const providerLabel = useCallback(
    (provider: AiProviderId) =>
      provider === 'anthropic'
        ? t('project.builder.ai.model.provider.anthropic')
        : t('project.builder.ai.model.provider.openai'),
    [t]
  );

  const proposalSummary = useMemo(() => {
    if (!pending) {
      return null;
    }

    if (pending.mode === 'full_design' && pending.document) {
      return t('project.builder.ai.diff.sections', { count: pending.document.sections.length });
    }

    if (pending.mode === 'edit_section' && pending.section) {
      return getSectionLabel(pending.section, t);
    }

    return null;
  }, [pending, t]);

  return (
    <div className={['av-builder-ai', isOpen ? 'is-open' : ''].filter(Boolean).join(' ')}>
      {isOpen ? (
        <section className="av-builder-ai__panel" role="dialog" aria-modal="false" aria-label={t('project.builder.ai.title')}>
          <header className="av-builder-ai__header">
            <div>
              <strong>{t('project.builder.ai.title')}</strong>
              <span>{t('project.builder.ai.subtitle')}</span>
            </div>
            <button
              type="button"
              className="av-builder-ai__close"
              onClick={handleToggleOpen}
              aria-label={t('project.builder.ai.bubble.close')}
            >
              <Icon name="times" />
            </button>
          </header>

          <div className="av-builder-ai__modes">
            {(['full_design', 'edit_section'] as BuilderChatMode[]).map((candidate) => (
              <button
                key={candidate}
                type="button"
                className={['av-filter-chip', mode === candidate ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => handleModeChange(candidate)}
              >
                {candidate === 'full_design'
                  ? t('project.builder.ai.modes.fullDesign')
                  : t('project.builder.ai.modes.editSection')}
              </button>
            ))}
          </div>

          <p className="av-builder-ai__hint">
            {mode === 'full_design'
              ? t('project.builder.ai.modeHints.fullDesign')
              : t('project.builder.ai.modeHints.editSection')}
          </p>

          <div className="av-builder-ai__context">
            {activePage ? (
              <span>{t('project.builder.ai.context.activePage', { title: activePage.document.pageMeta.title })}</span>
            ) : (
              <span>{t('project.builder.ai.context.noPage')}</span>
            )}
            {mode === 'edit_section' ? (
              activeSection ? (
                <span>{t('project.builder.ai.context.activeSection', { label: getSectionLabel(activeSection, t) })}</span>
              ) : (
                <span>{t('project.builder.ai.context.noSection')}</span>
              )
            ) : null}
          </div>

          {activeModels.length > 0 ? (
            <label className="av-builder-ai__model">
              <span>{t('project.builder.ai.model.label')}</span>
              <select value={selectedModelId} onChange={(event) => setSelectedModelId(event.target.value)}>
                {activeModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {providerLabel(model.provider)} - {model.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="av-builder-ai__prompt">
            <span>{t('project.builder.ai.prompt.label')}</span>
            <TextAreaField
              ref={promptRef}
              value={prompt}
              placeholder={t('project.builder.ai.prompt.placeholder')}
              rows={4}
              onValueChange={setPrompt}
              disabled={!hasAnyKey || isGenerating}
            />
            <small>{t('project.builder.ai.prompt.exampleHint')}</small>
          </label>

          {!hasAnyKey ? <p className="av-builder-ai__warning">{t('project.builder.ai.errors.noKey')}</p> : null}
          {error ? <p className="av-builder-ai__error">{error}</p> : null}
          {statusMessage && !error ? <p className="av-builder-ai__status">{statusMessage}</p> : null}

          {pending ? (
            <div className="av-builder-ai__diff">
              <div className="av-builder-ai__diff-header">
                <strong>{t('project.builder.ai.diff.title')}</strong>
                <span>{pending.cacheHit ? t('project.builder.ai.status.cached') : `${pending.latencyMs} ms`}</span>
              </div>
              <div className="av-builder-ai__diff-body">
                {pending.mode === 'full_design' ? (
                  <span>{proposalSummary}</span>
                ) : (
                  <span>
                    <strong>{t('project.builder.ai.diff.changedLabel')}</strong> - {proposalSummary}
                  </span>
                )}
              </div>
              <div className="av-builder-ai__diff-actions">
                <button type="button" className="av-secondary-button" onClick={handleDiscard}>
                  {t('project.builder.ai.actions.discard')}
                </button>
                <button type="button" className="av-primary-button" onClick={handleApply}>
                  {t('project.builder.ai.actions.apply')}
                </button>
              </div>
            </div>
          ) : null}

          <div className="av-builder-ai__footer">
            <button
              type="button"
              className="av-primary-button"
              disabled={!canSubmit}
              onClick={() => void handleGenerate()}
            >
              <Icon name={isGenerating ? 'sync' : 'avi'} />
              <span>{isGenerating ? t('project.builder.ai.actions.generating') : t('project.builder.ai.actions.generate')}</span>
            </button>
            <button
              type="button"
              className="av-secondary-button"
              onClick={handleUndo}
              disabled={aiUndoDepth === 0}
            >
              <Icon name="arrowUp" />
              <span>{t('project.builder.ai.actions.undo')}</span>
            </button>
          </div>
        </section>
      ) : null}

      <button
        type="button"
        className="av-builder-ai__trigger"
        onClick={handleToggleOpen}
        aria-expanded={isOpen}
        aria-label={isOpen ? t('project.builder.ai.bubble.close') : t('project.builder.ai.bubble.open')}
      >
        <Icon name={isOpen ? 'times' : 'avi'} />
        <span>{isOpen ? t('project.builder.ai.bubble.close') : t('project.builder.ai.bubble.open')}</span>
      </button>
    </div>
  );
}
